import React, { useState, useEffect } from 'react';
import { Zap, LogOut, User, Settings } from 'lucide-react';
import { Navbar, Container, Nav, NavDropdown, Badge, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import LogoutButton from './LogoutButton';
import { getCurrentUser } from '../../utils/auth';
// Correctly imported the new API function
import api, { getActiveTasksCount } from '../../api/api';

const AppHeader = ({ walletBalance }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getCurrentUser();
  const [activeTaskCount, setActiveTaskCount] = useState(0);
  const [tenantName, setTenantName] = useState("");

  const slug = user?.tenant_slug || null;
  const isAdmin = user?.role === 'tenant_admin';

  useEffect(() => {
    if (!slug) return;

    const fetchHeaderData = async () => {
      try {
        // 1. Fetch Wallet & Tenant Name (using updated backend)
        const walletRes = await api.get(`/tenant/${slug}/wallet/balance`);
        setTenantName(walletRes.data.tenant_name);

        // 2. Fetch Global Tasks if Admin
        if (isAdmin) {
          const taskRes = await getActiveTasksCount(slug);
          setActiveTaskCount(taskRes.data.count || 0);
        }
      } catch (err) {
        console.error("Header sync failed", err);
      }
    };

    fetchHeaderData();
    const interval = setInterval(fetchHeaderData, 10000); 
    return () => clearInterval(interval);
  }, [isAdmin, slug]);

  const isEditingAdmin = location.pathname.includes('/admin');

  return (
    <Navbar bg="white" expand="lg" className="shadow-sm border-bottom py-2 sticky-top">
      <Container fluid className="px-4">
        <Navbar.Brand 
          as={Link} 
          to={isAdmin && isEditingAdmin ? `/tenant/${slug}/admin` : `/`} 
          className="d-flex align-items-center"
        >
          <img src="/assets/images/logo.png" alt="Logo" height="80" className="me-2" />
          <div className="d-none d-sm-block">
            <span className="fw-bold text-primary">Academic<span style={{ color: "#B8962E" }}>OS</span></span>
            <small className="d-block text-muted" style={{ fontSize: '0.65rem', marginTop: '-5px' }}>
              Embracing Academic Intelligence
            </small>
          </div>

          {/* ✅ NEW: Welcome Message */}
          {tenantName && (
            <div className="ms-3 ps-3 border-start d-none d-md-block" style={{ borderColor: '#dee2e6' }}>
              <small className="text-muted d-block" style={{ fontSize: '0.7rem', lineHeight: '1' }}>Welcome,</small>
              <span className="fw-bold text-primary" style={{ fontSize: '0.9rem' }}>{tenantName}</span>
            </div>
          )}
        </Navbar.Brand>

        <Navbar.Toggle aria-controls="app-navbar-nav" />
        
        <Navbar.Collapse id="app-navbar-nav">
          <Nav className="me-auto ms-lg-4">
            {/* RESTORED: Menu 1: Resources */}
            <NavDropdown title="Resources" id="resources-dropdown">
              <NavDropdown.Item as={Link} to="/user-guide">User Guide</NavDropdown.Item>
              <NavDropdown.Item as={Link} to="/faq">FAQ</NavDropdown.Item>
              <NavDropdown.Divider />
              <NavDropdown.Item as={Link} to="/best-practices">Best Practices</NavDropdown.Item>
            </NavDropdown>

            {/* RESTORED: Menu 2: Frameworks */}
            <NavDropdown title="Frameworks" id="frameworks-dropdown">
              <NavDropdown.Item as={Link} to="/content-standards">Content Standards</NavDropdown.Item>
              <NavDropdown.Item as={Link} to="/taxonomy-standards">Taxonomy Standards</NavDropdown.Item>
            </NavDropdown>
            <Nav.Link 
              href="/release-notes" 
              className="d-flex align-items-center gap-2 text-dark fw-medium"
          >
              <Zap size={16} className="text-warning" /> 
              What's New
              <Badge bg="danger" pill style={{ fontSize: '10px' }}>New</Badge>
          </Nav.Link>

            {/* Role Switcher for Admins */}
            {isAdmin && (
                <NavDropdown 
                    title={<span className="text-primary fw-bold">🔄 Switch View</span>} 
                    id="role-switcher-dropdown"
                    className="ms-lg-2 px-2 border rounded bg-light"
                >
                    <NavDropdown.Header>Navigation Hub</NavDropdown.Header>
                    <NavDropdown.Item onClick={() => navigate(`/tenant/${slug}/dashboard`)}>
                        SME Production Pipeline
                    </NavDropdown.Item>
                    <NavDropdown.Item onClick={() => navigate(`/tenant/${slug}/admin`)}>
                        Tenant Administration
                    </NavDropdown.Item>
                </NavDropdown>
            )}
          </Nav>

          <Nav className="align-items-center gap-3">
            {/* Global Task Observer Pulse */}
            {isAdmin && activeTaskCount > 0 && (
                <OverlayTrigger
                    placement="bottom"
                    overlay={<Tooltip>Active AI Tasks across all Tenant Projects</Tooltip>}
                >
                    <Badge pill bg="warning" text="dark" className="d-flex align-items-center gap-2 px-3 py-2 border shadow-sm">
                        <Spinner animation="grow" size="sm" variant="dark" aria-hidden="true" />
                        <span className="fw-bold">{activeTaskCount} Tasks</span>
                    </Badge>
                </OverlayTrigger>
            )}

            {/* Wallet Info */}
            {walletBalance !== undefined && (
              <Badge bg="success" className="p-2 fw-medium border shadow-sm">
                Wallet: {walletBalance.toFixed(2)} Credits
              </Badge>
            )}
            
            <div className="text-end d-none d-lg-block me-2 border-start ps-3" style={{ borderColor: '#dee2e6' }}>
              <small className="text-muted d-block" style={{ fontSize: '0.7rem', lineHeight: '1.2' }}>Logged in as</small>
              <span className="fw-bold text-dark" style={{ fontSize: '0.85rem' }}>
                {user?.email || "User Account"}
              </span>
            </div>
            
            <LogoutButton />
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default AppHeader;
